import { Message } from '@arco-design/web-vue'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { COOKIE_LOGIN_KEY, LOGIN_FAILED_RETRY_COUNT } from '@/constant/cache-key'
import { loadSessionStorage, removeSessionStorage, saveSessionStorage } from '@/utils/cache'
import { i18n } from '@/plugins/i18n'
import type { RbResponse } from '@/types/misc/http'
import store from '@/store'
import { getAuthFailureFallbackUrl, getLoginEntryUrl, isLocalDebugDomain, resolveAuthRedirectUrl, saveLoginCallbackUrl } from '@/utils/auth-redirect'

interface ErrorResponseData extends Partial<RbResponse<unknown>> {
  return_msg?: string
}

function getFallbackErrorMessage(status: number) {
  switch (status) {
    case 401:
      return i18n.global.t('common.request.unauthorized')
    case 403:
      return i18n.global.t('common.request.forbidden')
    case 404:
      return i18n.global.t('common.request.notFound')
    default:
      return i18n.global.t('common.request.defaultError')
  }
}

async function normalizeErrorResponseData(data: unknown): Promise<ErrorResponseData | null> {
  if (!data) {
    return null
  }

  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const rawText = (await data.text()).trim()
    if (!rawText) {
      return null
    }

    try {
      return JSON.parse(rawText) as ErrorResponseData
    }
    catch {
      return {
        msg: rawText,
      }
    }
  }

  if (typeof data === 'string') {
    const rawText = data.trim()
    if (!rawText) {
      return null
    }

    try {
      return JSON.parse(rawText) as ErrorResponseData
    }
    catch {
      return {
        msg: rawText,
      }
    }
  }

  if (typeof data === 'object') {
    return data as ErrorResponseData
  }

  return null
}

export async function resolveErrorMessage(err: Pick<AxiosResponse<unknown>, 'status' | 'data'>) {
  const fallbackMessage = getFallbackErrorMessage(err.status)
  const normalizedData = await normalizeErrorResponseData(err.data)
  return normalizedData?.return_msg || normalizedData?.msg || fallbackMessage
}

export async function throwErrorMessage(err: AxiosResponse<ErrorResponseData>) {
  const message = await resolveErrorMessage(err)

  if (err.status === 401) {
    Message.warning(message)
  }
  else {
    Message.error(message)
  }

  return Promise.reject(message)
}

export function clearCookies(preserveKeys: string[] = []) {
  return new Promise((resolve) => {
    const cookies = document.cookie.split(';')
    cookies.forEach((cookie) => {
      const [rawKey] = cookie.split('=')
      const key = rawKey?.trim()
      if (!key || preserveKeys.includes(key)) {
        return
      }
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    })
    resolve(true)
  })
}

const AUTH_RECOVERY_REQUEST_MARKERS = [
  '/blade-auth/cas/token',
  '/blade-auth/cas/refresh',
  '/blade-auth/cas/getPermissionGroupList',
  '/cas/logout',
  '/api/casApi/go-cas/anon/login/tgc',
  '/callbackWithoutRedirect',
]

let renewalPromise: Promise<boolean> | null = null

interface AuthRequestConfig extends AxiosRequestConfig {
  authReplayAttempted?: boolean
  skipAuthRenewal?: boolean
}

export function isTerminalAuthFailureReason(reason?: string | null) {
  return reason === 'permission_revoked' || reason === 'account_disabled'
}

export async function handleTerminalAuthFailure(reason: string | null | undefined) {
  if (!isTerminalAuthFailureReason(reason)) {
    return false
  }

  const authStore = store.authStore
  authStore.logoutCallBack()
  removeSessionStorage(LOGIN_FAILED_RETRY_COUNT)

  if (isLocalDebugDomain()) {
    return false
  }

  await clearCookies([COOKIE_LOGIN_KEY])
  const fallbackReason = reason === 'permission_revoked' ? 'no-permission' : 'auth-failed'
  window.location.replace(getAuthFailureFallbackUrl(fallbackReason))
  return false
}

export function isAuthRecoveryRequestUrl(url?: string) {
  if (!url) {
    return false
  }
  return AUTH_RECOVERY_REQUEST_MARKERS.some(marker => url.includes(marker))
}

async function tryRenewal() {
  const authStore = store.authStore

  try {
    const refreshed = await authStore.ensureSession('unauthorized')
    if (!refreshed) {
      if (isTerminalAuthFailureReason(authStore.lastSessionRenewalFailureReason)) {
        return false
      }
      await authStore.renewalRoles()
    }
    removeSessionStorage(LOGIN_FAILED_RETRY_COUNT)
    return true
  }
  catch (error) {
    console.error('handleTokenExpired renewal error', error)
    return false
  }
}

export async function handleTokenExpired(err: AxiosResponse<RbResponse<unknown>>) {
  const authStore = store.authStore
  const loginFailedRetryCount = loadSessionStorage(LOGIN_FAILED_RETRY_COUNT, 0) || 0
  const isStpPermissionDenied = (err.data as { data?: { error_code?: number } } | undefined)?.data?.error_code === 5001
  const isTgcInvalid = err.data?.return_msg === 'tgc invalid'
  const authFailureReason = (err.data as { reason?: string, data?: { reason?: string } } | undefined)?.reason
    ?? (err.data as { data?: { reason?: string } } | undefined)?.data?.reason
  let terminalAuthFailureReason = isTerminalAuthFailureReason(authFailureReason) ? authFailureReason : null
  const authRedirectUrl = resolveAuthRedirectUrl(err.data?.content)
  const requestUrl = err.config?.url
  const requestConfig = err.config as AuthRequestConfig | undefined
  const isAuthRecoveryRequest = isAuthRecoveryRequestUrl(requestUrl)
  const shouldSkipSilentRenewal = requestConfig?.skipAuthRenewal || requestConfig?.authReplayAttempted

  if (isAuthRecoveryRequest) {
    return false
  }

  if (!isStpPermissionDenied && !isTgcInvalid && !terminalAuthFailureReason && !shouldSkipSilentRenewal) {
    if (renewalPromise) {
      const renewed = await renewalPromise
      if (renewed) {
        return true
      }
    }
    else {
      renewalPromise = tryRenewal()
        .finally(() => {
          renewalPromise = null
        })

      const renewed = await renewalPromise
      if (renewed) {
        return true
      }
    }
    const renewalFailureReason = authStore.lastSessionRenewalFailureReason
    if (isTerminalAuthFailureReason(renewalFailureReason)) {
      terminalAuthFailureReason = renewalFailureReason
    }
  }

  if (isStpPermissionDenied) {
    return handleTerminalAuthFailure('permission_revoked')
  }

  if (terminalAuthFailureReason) {
    return handleTerminalAuthFailure(terminalAuthFailureReason)
  }

  const message = i18n.global.t('common.request.unauthorized')
  Message.warning(message)
  authStore.logoutCallBack()
  // 保存当前页面的路径，登录后返回
  saveLoginCallbackUrl()

  // 本地开发时不进行跳转登录页
  if (isLocalDebugDomain()) {
    return false
  }

  if (loginFailedRetryCount >= 3) {
    Message.warning(i18n.global.t('common.request.maxTry'))
    removeSessionStorage(LOGIN_FAILED_RETRY_COUNT)
    await clearCookies([COOKIE_LOGIN_KEY])
    window.location.replace(getAuthFailureFallbackUrl('auth-failed'))
    return false
  }

  if (authRedirectUrl) {
    // 跳转到登录页面 并带上当前的语言配置
    window.location.replace(authRedirectUrl)
  }
  else {
    // 后端未返回登录页地址时，走统一的 CAS 登录入口，而不是回到 STP 首页。
    saveSessionStorage(LOGIN_FAILED_RETRY_COUNT, loginFailedRetryCount + 1)
    // 保留登录回跳 cookie，避免清理会话时把回跳地址一起删掉。
    await clearCookies([COOKIE_LOGIN_KEY])
    window.location.replace(getLoginEntryUrl())
  }

  return false
}
